<template>
  <div class="class-page">
    <header class="page-heading">班次查询</header>

    <main class="class-content">
      <div v-if="loading" class="state-message">班次加载中...</div>
      <div v-else-if="!shiftList.length" class="state-message">暂无班次信息</div>
      <template v-else>
        <article
          v-for="item in shiftList"
          :key="item.id"
          class="shift-card"
          :class="{ selected: selectedShiftId === item.id }"
          @click="openActionSheet(item)"
        >
          <div class="shift-card-top">
            <h2>{{ item.name }}</h2>
            <span :class="`shift-${item.shiftKey}`">{{ item.shiftName }}</span>
          </div>
          <div class="shift-time">
            <span>开始时间: {{ item.startTime }}</span>
            <span>结束时间: {{ item.endTime }}</span>
          </div>
        </article>
      </template>
    </main>

    <transition name="sheet">
      <div
        v-if="selectedShift"
        class="action-mask"
        role="presentation"
        @click.self="closeActionSheet"
      >
        <section class="action-sheet" role="dialog" aria-modal="true">
          <header>
            <div>
              <strong>{{ selectedShift.name }}</strong>
              <span>{{ selectedShift.isOnDuty ? "当前上班" : "当前下班" }}</span>
            </div>
            <button type="button" aria-label="关闭弹窗" @click="closeActionSheet">×</button>
          </header>

          <button
            type="button"
            class="duty-button"
            :class="{ off: selectedShift.isOnDuty }"
            :disabled="submitting"
            @click="changeDutyStatus"
          >
            {{ submitting ? "处理中..." : selectedShift.isOnDuty ? "下班" : "上班" }}
          </button>
          <button type="button" class="close-sheet-button" @click="closeActionSheet">
            关闭弹窗
          </button>
        </section>
      </div>
    </transition>
  </div>
</template>

<script>
import { getJishiClassList, jishiDuty } from "@/api/Jishi";
import { handleLocalStorage } from "@/utils";
import { VPN_ID } from "@/api/login";

export default {
  name: "JiShiClass",
  data() {
    return {
      loading: false,
      submitting: false,
      selectedShiftId: null,
      shiftList: [
        {
          id: "class-early",
          technicianCode: "001",
          name: "中医技师",
          shiftName: "早班",
          shiftKey: "early",
          startTime: "09:30:00",
          endTime: "11:30:00",
          isOnDuty: true,
          raw: {},
        },
        {
          id: "class-spa",
          technicianCode: "002",
          name: "SPA技师",
          shiftName: "中班",
          shiftKey: "middle",
          startTime: "11:30:00",
          endTime: "16:30:00",
          isOnDuty: true,
          raw: {},
        },
        {
          id: "class-massage",
          technicianCode: "003",
          name: "按摩技师",
          shiftName: "中班",
          shiftKey: "middle",
          startTime: "11:30:00",
          endTime: "16:30:00",
          isOnDuty: false,
          raw: {},
        },
        {
          id: "class-bath",
          technicianCode: "004",
          name: "搓澡技师",
          shiftName: "晚班",
          shiftKey: "late",
          startTime: "16:30:00",
          endTime: "21:30:00",
          isOnDuty: true,
          raw: {},
        },
        {
          id: "class-beauty",
          technicianCode: "005",
          name: "美甲技师",
          shiftName: "晚班",
          shiftKey: "late",
          startTime: "16:30:00",
          endTime: "21:30:00",
          isOnDuty: false,
          raw: {},
        },
      ],
    };
  },
  computed: {
    /**
     * 根据当前选择的班次 id 获取完整班次对象。
     * 只保存 id 可以确保接口刷新列表后，底部弹窗始终使用列表中的最新数据。
     *
     * @returns {Object|null} 当前选中班次；未选择时返回 null。
     */
    selectedShift() {
      return (
        this.shiftList.find((item) => item.id === this.selectedShiftId) || null
      );
    },
  },
  created() {
    this.queryShiftList();
  },
  methods: {
    /**
     * 从接口读取门店的动态班次数据。
     * VpnId 优先读取项目统一存储键，兼容旧数据中的 vpnInfo.id；接口可能直接
     * 返回数组，也可能包装在 Items、Data 或 List 中，因此统一提取后再标准化。
     */
    async queryShiftList() {
      const storedVpnInfo = handleLocalStorage.get("vpnInfo") || {};
      const vpnId = handleLocalStorage.get(VPN_ID) || storedVpnInfo.id;
      if (!vpnId) {
        this.$toast("缺少门店信息");
        return;
      }

      this.loading = true;
      try {
        const response = await getJishiClassList({ VpnId: vpnId });
        const sourceList = Array.isArray(response)
          ? response
          : response.Items || response.Data || response.List || [];
        // 接口存在有效班次时替换示例数据；空数组时保留示例数据，方便页面联调。
        if (sourceList.length) {
          this.shiftList = sourceList.map((item, index) =>
            this.normalizeShiftItem(item, index)
          );
        }
      } catch (error) {
        // 请求失败时保留 data 中的示例班次，避免联调阶段页面完全空白。
        this.$toast("班次加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    /**
     * 将不同接口命名风格的班次字段转换为页面统一模型。
     * isOnDuty 同时兼容布尔值、数字 1、字符串“1/上班/onDuty”；shiftKey 用于
     * 早班、中班、晚班标签配色，无法识别时使用默认样式。
     *
     * @param {Object} item 接口返回的原始班次。
     * @param {number} index 当前数组下标，用于缺少 id 时生成稳定兜底值。
     * @returns {Object} 页面可直接展示和操作的标准班次对象。
     */
    normalizeShiftItem(item, index) {
      const shiftName =
        item.ShiftName || item.ClassName || item.shiftName || item.className || "";
      const rawDuty =
        item.IsOnDuty !== undefined
          ? item.IsOnDuty
          : item.isOnDuty !== undefined
          ? item.isOnDuty
          : item.DutyStatus;
      const isOnDuty =
        rawDuty === true ||
        rawDuty === 1 ||
        rawDuty === "1" ||
        rawDuty === "上班" ||
        rawDuty === "onDuty";

      return {
        id: item.Id || item.id || item.JiShiCode || item.EmployCode || index,
        technicianCode:
          item.JiShiCode || item.EmployCode || item.technicianCode || "",
        name:
          item.Name ||
          item.JiShiTypeName ||
          item.TechnicianTypeName ||
          item.name ||
          "未命名班次",
        shiftName: shiftName || "班次",
        shiftKey: this.getShiftKey(shiftName),
        startTime:
          item.StartTime || item.BeginTime || item.startTime || "--:--:--",
        endTime: item.EndTime || item.FinishTime || item.endTime || "--:--:--",
        isOnDuty,
        raw: item,
      };
    },
    /**
     * 根据班次中文名称返回样式键。
     *
     * @param {string} shiftName 班次名称。
     * @returns {string} early、middle、late 或 default。
     */
    getShiftKey(shiftName) {
      if (shiftName.includes("早")) return "early";
      if (shiftName.includes("中")) return "middle";
      if (shiftName.includes("晚")) return "late";
      return "default";
    },
    /**
     * 选中班次并打开底部操作弹窗。
     *
     * @param {Object} item 用户点击的标准班次对象。
     */
    openActionSheet(item) {
      this.selectedShiftId = item.id;
    },
    /**
     * 关闭底部弹窗并取消卡片选择。
     */
    closeActionSheet() {
      if (this.submitting) return;
      this.selectedShiftId = null;
    },
    /**
     * 根据当前状态执行相反的上下班操作。
     * 当前上班时提交“下班”，当前下班时提交“上班”；接口成功后只更新当前项，
     * 无需重新请求整个列表，并通过 duty-change 事件向父页面同步操作结果。
     */
    async changeDutyStatus() {
      if (!this.selectedShift || this.submitting) return;

      const shift = this.selectedShift;
      const targetOnDuty = !shift.isOnDuty;
      const storedVpnInfo = handleLocalStorage.get("vpnInfo") || {};
      const vpnId = handleLocalStorage.get(VPN_ID) || storedVpnInfo.id;
      this.submitting = true;
      try {
        await jishiDuty({
          JiShiCode: shift.technicianCode,
          DutyType: targetOnDuty ? "上班" : "下班",
          VpnId: vpnId,
        });
        shift.isOnDuty = targetOnDuty;
        this.$emit("duty-change", {
          shift: { ...shift },
          dutyType: targetOnDuty ? "上班" : "下班",
        });
        this.$toast.success("操作成功");
        this.selectedShiftId = null;
      } catch (error) {
        this.$toast("操作失败，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.class-page {
  min-height: 100vh;
  padding: 30px 0;
  box-sizing: border-box;
  background: #f7f1e7;
}

.page-heading {
  height: 86px;
  padding: 0 28px;
  border-radius: 24px 24px 0 0;
  box-sizing: border-box;
  color: #292c31;
  background: #fff;
  font-size: 31px;
  font-weight: 600;
  line-height: 86px;
}

.class-content {
  min-height: calc(100vh - 146px);
  padding: 24px 13px 60px;
  border-radius: 0 0 22px 22px;
  box-sizing: border-box;
  background: #fff;
}

.shift-card {
  margin-bottom: 24px;
  padding: 17px 15px 18px;
  border: 2px solid transparent;
  border-radius: 14px;
  box-sizing: border-box;
  background: #faf8f4;
  box-shadow: 0 2px 8px rgba(91, 75, 54, 0.04);
  transition: border-color 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &.selected {
    border-color: #ef6069;
  }
}

.shift-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h2 {
    margin: 0;
    color: #2d3034;
    font-size: 29px;
    font-weight: 500;
  }

  span {
    padding: 9px 16px;
    border-radius: 17px 17px 4px 17px;
    color: #fff;
    font-size: 21px;
  }
}

.shift-early {
  background: linear-gradient(90deg, #13bbae, #21d2bd);
}

.shift-middle {
  background: linear-gradient(90deg, #665cf0, #8a7aff);
}

.shift-late {
  background: linear-gradient(90deg, #1979dc, #2b96f5);
}

.shift-default {
  background: linear-gradient(90deg, #8a8ea0, #aeb2c0);
}

.shift-time {
  display: flex;
  margin-top: 20px;
  justify-content: space-between;
  color: #37393d;
  font-size: 22px;
}

.action-mask {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: flex-end;
  background: rgba(0, 0, 0, 0.42);
}

.action-sheet {
  width: 100%;
  padding: 0 24px calc(24px + env(safe-area-inset-bottom));
  border-radius: 26px 26px 0 0;
  box-sizing: border-box;
  background: #f7f4ef;

  header {
    display: flex;
    height: 92px;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #e7ded2;
  }

  header div {
    display: flex;
    align-items: baseline;
  }

  header strong {
    font-size: 29px;
  }

  header span {
    margin-left: 18px;
    color: #aa915d;
    font-size: 22px;
  }

  header button {
    width: 50px;
    height: 50px;
    border: 0;
    color: #8a8176;
    background: transparent;
    font-size: 42px;
  }
}

.duty-button,
.close-sheet-button {
  width: 100%;
  height: 76px;
  border-radius: 10px;
  font-size: 27px;
}

.duty-button {
  margin: 24px 0 18px;
  border: 0;
  color: #fff;
  background: linear-gradient(90deg, #d0ad68, #f0c875);

  &.off {
    background: linear-gradient(90deg, #e5545e, #f27d84);
  }

  &:disabled {
    opacity: 0.65;
  }
}

.close-sheet-button {
  border: 1px solid #ded7ca;
  color: #6f685e;
  background: #fff;
}

.sheet-enter-active,
.sheet-leave-active {
  transition: background-color 0.2s ease;

  .action-sheet {
    transition: transform 0.2s ease;
  }
}

.sheet-enter,
.sheet-leave-to {
  background: rgba(0, 0, 0, 0);

  .action-sheet {
    transform: translateY(100%);
  }
}

.state-message {
  padding: 100px 0;
  color: #aaa196;
  font-size: 25px;
  text-align: center;
}
</style>
